// @flow
import type { DigitalFile } from './model'

const BROWSER_VIEWABLE_TYPES: Set<string> = new Set([
  'application/pdf',
  'text/plain',
])

const IMAGE_TYPE_PREFIX = 'image/'

const IMAGE_EXTS: Set<string> = new Set([
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'svg',
])

const XLSX_TYPES: Set<string> = new Set([
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
])
const XLSX_EXTS: Set<string> = new Set(['xlsx', 'xls'])

const DOCX_TYPES: Set<string> = new Set([
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
])
const DOCX_EXTS: Set<string> = new Set(['docx'])

const CSV_TYPES: Set<string> = new Set(['text/csv', 'application/csv'])
const CSV_EXTS: Set<string> = new Set(['csv'])

const fileExt = (name: ?string): ?string => {
  if (!name) return null
  const dot = name.lastIndexOf('.')
  if (dot < 0) return null
  const ext = name.slice(dot + 1).toLowerCase()
  return ext || null
}

const matches = (
  contentType: ?string,
  name: ?string,
  types: Set<string>,
  exts: Set<string>,
): boolean => {
  if (contentType && types.has(contentType)) return true
  const ext = fileExt(name)
  return Boolean(ext && exts.has(ext))
}

export const isImage = (contentType: ?string, name: ?string): boolean => {
  if (contentType && contentType.startsWith(IMAGE_TYPE_PREFIX)) return true
  const ext = fileExt(name)
  return Boolean(ext && IMAGE_EXTS.has(ext))
}

export const isXlsx = (contentType: ?string, name: ?string): boolean =>
  matches(contentType, name, XLSX_TYPES, XLSX_EXTS)

export const isDocx = (contentType: ?string, name: ?string): boolean =>
  matches(contentType, name, DOCX_TYPES, DOCX_EXTS)

export const isCsv = (contentType: ?string, name: ?string): boolean =>
  matches(contentType, name, CSV_TYPES, CSV_EXTS)

const isBrowserViewable = (contentType: ?string): boolean =>
  Boolean(contentType) && BROWSER_VIEWABLE_TYPES.has(contentType)

export const isViewable = (file: DigitalFile): boolean =>
  isImage(file.content_type, file.name) ||
  isXlsx(file.content_type, file.name) ||
  isDocx(file.content_type, file.name) ||
  isCsv(file.content_type, file.name) ||
  isBrowserViewable(file.content_type)

const BYTE_UNITS = ['KB', 'MB', 'GB', 'TB']

export const formatBytes = (val: ?number, precision: number = 1): string => {
  if (val == null || isNaN(val)) return ''
  let size = val / 1024
  let unit = 0
  while (size >= 1024 && unit < BYTE_UNITS.length - 1) {
    size /= 1024
    unit += 1
  }
  return `${size.toFixed(precision)} ${BYTE_UNITS[unit]}`
}

export const extLabel = (file: DigitalFile): string => {
  const ext = fileExt(file.name)
  if (ext) return ext.toUpperCase()
  if (file.content_type) {
    const sub = file.content_type.split('/')[1]
    if (sub) return sub.toUpperCase()
  }
  return 'FILE'
}

export const fileSubtitle = (file: DigitalFile): string => {
  const parts = [extLabel(file)]
  const size = formatBytes(file.byte_size)
  if (size) parts.push(size)
  return parts.join(' · ')
}
